import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";

export interface KnowledgeBaseArticle {
    id: string;
    title: string;
    lastModified?: string;
    excerpt?: string;
    body?: string;
    version?: {
        when: string;
        by: {
            displayName: string;
        };
    };
    ancestors?: Array<{
        id: string;
        title: string;
    }>;
}

export interface KnowledgePagination {
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
}

export interface KnowledgeBaseResponse {
    pages: { [key: string]: KnowledgeBaseArticle[] };
    pagination: KnowledgePagination;
}

export interface SearchResponse {
    searchResults: KnowledgeBaseArticle[];
    totalSize: number;
    nextCursor?: string;
}

export const MAX_SEARCH_TERM_LENGTH = 200;

class KnowledgeBaseService {
    async fetchKnowledgeBase(language: string, cursor?: string, limit = 100): Promise<KnowledgeBaseResponse> {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (cursor) {
            params.append("cursor", cursor);
        }

        const response = (await apiGatewayService.invokeApi(
            `/api/v1/knowledge-base/${language}?${params.toString()}`,
            "GET",
            null,
            {
                apiType: ApiType.ARTHUR,
            }
        )) as {
            root_pages?: { [key: string]: KnowledgeBaseArticle[] };
            next_cursor?: string;
        };

        // Handle API response format - root_pages is always an object
        const pages = response?.root_pages || {};

        return {
            pages,
            pagination: {
                limit,
                hasMore: !!response?.next_cursor,
                nextCursor: response?.next_cursor || null,
            },
        };
    }

    async fetchPageById(language: string, pageId: string): Promise<KnowledgeBaseArticle> {
        const response = (await apiGatewayService.invokeApi(
            `/api/v1/knowledge-base/${language}?page_id=${pageId}`,
            "GET",
            null,
            {
                apiType: ApiType.ARTHUR,
            }
        )) as { page_content: KnowledgeBaseArticle & { ancestors?: Array<{ id: string; title: string }> } };
        return { ...response.page_content, ancestors: response.page_content.ancestors };
    }

    async fetchPageChildren(
        language: string,
        parentPageId: string,
        cursor?: string,
        limit = 100
    ): Promise<{ childPages: KnowledgeBaseArticle[]; nextCursor?: string }> {
        const params = new URLSearchParams({
            parent_page_id: parentPageId,
            limit: limit.toString(),
        });
        if (cursor) {
            params.append("cursor", cursor);
        }

        const response = (await apiGatewayService.invokeApi(
            `/api/v1/knowledge-base/${language}?${params.toString()}`,
            "GET",
            null,
            {
                apiType: ApiType.ARTHUR,
            }
        )) as { child_pages?: KnowledgeBaseArticle[]; next_cursor?: string };
        return {
            childPages: response.child_pages || [],
            nextCursor: response.next_cursor,
        };
    }

    async searchPages(language: string, searchTerm: string, cursor?: string, limit = 100): Promise<SearchResponse> {
        if (searchTerm.length > MAX_SEARCH_TERM_LENGTH) {
            throw new Error(`Search term exceeds maximum length of ${MAX_SEARCH_TERM_LENGTH} characters.`);
        }

        const params = new URLSearchParams({
            search: searchTerm,
            limit: limit.toString(),
        });
        if (cursor) {
            params.append("cursor", cursor);
        }

        const response = (await apiGatewayService.invokeApi(
            `/api/v1/knowledge-base/${language}?${params.toString()}`,
            "GET",
            null,
            {
                apiType: ApiType.ARTHUR,
            }
        )) as { search_results?: KnowledgeBaseArticle[]; total_size?: number; next_cursor?: string };
        return {
            searchResults: response.search_results || [],
            totalSize: response.total_size || 0,
            nextCursor: response.next_cursor,
        };
    }
}

export const knowledgeBaseService = new KnowledgeBaseService();
